import { type MutableCollabFilter } from '@databases/model/views/view-filter';
import { type DatabaseFilter } from '@databases/types/database/view';

import { isUsageFilter } from './isUsageFilter.ts';
import { getFilterById } from './search.ts';

/**
  Retrieves a nested filter based on the provided id. The filter is retrieved from the
  list of filters stored in the view of a database. Two approaches are used to retrieve
  the filter:

    1. An expected pathing is provided when a filter attribute is altered or a filter
    is deleted. This pathing is provided when the operation occurs, using the path to
    make a quick look up of the filter and validate its id against the expected id.

    2. Linear search is performed on the provided filter group, determining if a filter
    with the provided id exists.
  
  @param filters - the list of filters for a database view
  @param id - the id of the filter that should be retrieved
  @param expectedIndexPath - the expected index pathing for the filter used as a baseline
  to find the filter in the filters array

  @returns {[MutableCollabFilter | DatabaseFilter, number]} - [filter, indexInFilterGroup]
  the filter which matches the provided id and the index of the filter within its direct
  filter group
  @returns {undefined} - when no filter with the expected id is present in the list of
  filters stored in the view of a database  
*/
export function getNestedFilter(
  filters: MutableCollabFilter[] | DatabaseFilter[],
  id: string,
  expectedIndexPath: number[]
): [MutableCollabFilter | DatabaseFilter, number] | undefined {
  let filterGroup = filters;
  let depth = 0;

  for (depth; depth < expectedIndexPath.length; depth++) {
    const currentFilter = filterGroup[expectedIndexPath[depth]];

    // Ensure manipulation has not occurred that has removed filter at the expected index
    if (currentFilter) {
      if (currentFilter.id === id) {
        return [currentFilter, expectedIndexPath[depth]];
      } else if (isUsageFilter(currentFilter)) {
        filterGroup = currentFilter.value;
      }
    }
  }

  // Use linear search to find the filter with the given id if one exists and was not in
  // the expected path
  return getFilterById(filters, id);
}
